import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { IdFormContext } from '../../App/App';
import { DraftApi } from '../../Services/DraftService';
import { EurekaDraft } from '../../@Types/Draft/Draft';
import { convertFromRaw, EditorState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import { produce } from 'immer';
import CustomContext from '../../Contexts/CustomContext';
import { FormDecoratorsStrategy, FormDecorator } from './FormDecorator';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAppSelector } from '../../hooks';

export const DraftLoadingContext = createContext(true);

interface SmartDraftRendererProps {
    property: string;
    draft: EurekaDraft;
    margin?: React.CSSProperties['margin'];
    dependencies: Record<string, any>;
    onChange?: (value: EurekaDraft) => void;
}

function SmartDraftRenderer({
    draft,
    margin,
    onChange,
    property,
    dependencies,
}: SmartDraftRendererProps): JSX.Element {
    const idForm = useContext(IdFormContext);

    const mapDraftEntities = useContext(CustomContext).mapDraftEntities;
    const postview = useAppSelector((state) => state.global.postview);

    const hasEntities = useMemo(
        () => Object.keys(draft.entityMap).length > 0,
        [draft]
    );

    const { data: mapped, isFetching } = DraftApi.useMapDraftQuery(
        !hasEntities || postview
            ? skipToken
            : {
                  idForm,
                  property,
                  dependencies,
                  mapDraftEntities,
              }
    );

    useEffect(() => {
        if (mapped && !postview) onChange?.(mapped);
    }, [mapped]);

    const { editorState, isEmpty } = useMemo(() => {
        const content = convertFromRaw(
            cleanUpDraftParams(isFetching ? draft : mapped ?? draft)
        );
        return {
            editorState: EditorState.createWithContent(content),
            isEmpty: !content.hasText(),
        };
    }, [mapped, draft, isFetching]);

    if (isEmpty) return <></>;

    return (
        <div style={{ margin }}>
            <DraftLoadingContext.Provider value={!postview && isFetching}>
                <Editor
                    readOnly
                    toolbarHidden
                    editorState={editorState}
                    editorClassName={'Erk-Forms-Draft'}
                    customDecorators={customDecorators}
                />
            </DraftLoadingContext.Provider>
        </div>
    );
}

export default SmartDraftRenderer;

function cleanUpDraftParams(draft: EurekaDraft): EurekaDraft {
    return produce(draft, (draft) => {
        for (const block of draft.blocks) {
            for (const entity of block.entityRanges) {
                const { offset, length } = entity;
                //Change text into spaces
                block.text = block.text.replace(
                    block.text.substring(offset, offset + length),
                    ' '.repeat(length)
                );
            }
        }
    });
}

const customDecorators = [
    {
        strategy: FormDecoratorsStrategy,
        component: FormDecorator,
    },
];
